import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink, Plane, Ship, Trash2, Sparkles } from "lucide-react";
import { api, ALL_STATUSES, formatApiError, formatDate, formatDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { ShipmentLabel } from "@/components/ShipmentLabel";
import { Timeline } from "@/components/Timeline";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-slate-100 py-2 last:border-0">
    <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-right text-sm font-semibold text-slate-800">{value || "—"}</span>
  </div>
);

export default function ShipmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete = user?.role === "admin";
  const [shipment, setShipment] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(null);

  const draft = async (kind) => {
    setDrafting(kind);
    try {
      const { data } = await api.post(`/shipments/${id}/ai-draft`, { kind, status: newStatus });
      setNote(data.text);
      toast.success(kind === "note" ? "Note rédigée par l'IA" : "Message client rédigé");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDrafting(null);
    }
  };

  const load = useCallback(() => {
    api
      .get(`/shipments/${id}`)
      .then(({ data }) => {
        setShipment(data);
        setNewStatus(data.status);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [id]);

  useEffect(load, [load]);

  const updateStatus = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/shipments/${id}/status`, { status: newStatus, note: note || null });
      setShipment(data);
      setNote("");
      toast.success("Statut mis à jour");
      if (data.email_sent) toast.success("Email de notification envoyé au destinataire");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/shipments/${id}`);
      toast.success("Livraison supprimée");
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (!shipment) {
    return <div className="p-10 text-center text-slate-500" data-testid="shipment-loading">Chargement…</div>;
  }

  const TypeIcon = shipment.shipping_type === "aerien" ? Plane : Ship;

  return (
    <div className="min-h-screen bg-[#F8FAFC] px-4 py-8 sm:px-8" data-testid="shipment-detail">
      <div className="mx-auto max-w-5xl">
        <Button variant="ghost" className="mb-4 gap-2 pl-0 text-sm text-slate-600"
                onClick={() => navigate("/admin")} data-testid="detail-back-button">
          <ArrowLeft className="h-4 w-4" /> Retour au tableau de bord
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-mono text-2xl font-extrabold tracking-wider text-[#1E3A8A] sm:text-3xl"
                data-testid="detail-tracking-number">
              {shipment.tracking_number}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <StatusBadge status={shipment.status} testId="detail-status-badge" />
              <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                <TypeIcon className="h-4 w-4 text-[#2563EB]" />
                {shipment.shipping_type === "aerien" ? "Aérien" : "Maritime"}
              </span>
            </div>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-slate-500"
               data-testid="detail-activity-log">
              Créé le {formatDateTime(shipment.created_at)}
              {shipment.created_by ? ` par ${shipment.created_by}` : ""}
              {shipment.updated_at
                ? ` · Dernière modification ${formatDateTime(shipment.updated_at)}${
                    shipment.updated_by ? ` par ${shipment.updated_by}` : ""
                  }`
                : ""}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="gap-2 border-slate-300"
                    onClick={() => window.open(`/suivi/${shipment.tracking_number}`, "_blank")}
                    data-testid="view-public-page-button">
              <ExternalLink className="h-4 w-4" /> Vue publique
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" disabled={!canDelete}
                        title={canDelete ? "Supprimer" : "Réservé à l'administrateur principal"}
                        className="gap-2 border-rose-300 text-[#DC2626] hover:bg-rose-50"
                        data-testid="delete-shipment-button">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer cette livraison ?</AlertDialogTitle>
                  <AlertDialogDescription>
                    La livraison {shipment.tracking_number} et son historique seront définitivement
                    supprimés. Cette action est irréversible.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="cancel-delete-button">Annuler</AlertDialogCancel>
                  <AlertDialogAction onClick={remove}
                                     className="bg-[#DC2626] text-white hover:bg-[#b91c1c]"
                                     data-testid="confirm-delete-button">
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-5">
          <div className="space-y-6 lg:col-span-3">
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Mettre à jour le statut</h2>
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    Nouveau statut
                  </Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="mt-2 h-11" data-testid="status-update-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-[10px] uppercase tracking-wider text-slate-500">
                    Note (optionnel)
                  </Label>
                  <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-2"
                            placeholder="Ex. Colis dédouané, en attente du livreur"
                            data-testid="status-note-input" />
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={drafting}
                            className="gap-2 border-blue-300 text-[#1E3A8A] hover:bg-blue-50"
                            onClick={() => draft("note")} data-testid="ai-draft-note-button">
                      <Sparkles className="h-4 w-4" />
                      {drafting === "note" ? "Rédaction…" : "Rédiger la note avec l'IA"}
                    </Button>
                    <Button type="button" variant="outline" size="sm" disabled={drafting}
                            className="gap-2 border-slate-300"
                            onClick={() => draft("client_message")}
                            data-testid="ai-draft-message-button">
                      <Sparkles className="h-4 w-4" />
                      {drafting === "client_message" ? "Rédaction…" : "Message client"}
                    </Button>
                  </div>
                </div>
                <Button onClick={updateStatus} disabled={saving}
                        className="h-11 w-full bg-[#2563EB] font-bold text-white hover:bg-[#1D4ED8] sm:w-auto sm:px-8"
                        data-testid="status-update-submit">
                  {saving ? "Enregistrement…" : "Enregistrer le statut"}
                </Button>
              </div>
            </section>

            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-slate-900">Historique</h2>
              <div className="mt-6">
                <Timeline history={shipment.history} showAuthor />
              </div>
            </section>
          </div>

          <div className="space-y-6 lg:col-span-2">
            <ShipmentLabel shipment={shipment} />
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">Colis</h3>
              <div className="mt-3">
                <Row label="Poids" value={`${shipment.weight_kg} kg`} />
                <Row label="Dimensions" value={shipment.dimensions} />
                <Row label="Contenu" value={shipment.content} />
                <Row label="Valeur déclarée" value={shipment.declared_value ? `${shipment.declared_value} CAD` : "—"} />
                <Row label="Destination" value={shipment.destination_city} />
                <Row label="Expédition" value={formatDate(shipment.shipped_date)} />
                <Row label="Livraison est." value={formatDate(shipment.estimated_delivery)} />
              </div>
            </section>
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">Expéditeur</h3>
              <div className="mt-3">
                <Row label="Nom" value={shipment.sender.name} />
                <Row label="Téléphone" value={shipment.sender.phone} />
                <Row label="Adresse" value={shipment.sender.address} />
              </div>
            </section>
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900">Destinataire</h3>
              <div className="mt-3">
                <Row label="Nom" value={shipment.recipient.name} />
                <Row label="Téléphone" value={shipment.recipient.phone} />
                <Row label="Adresse" value={shipment.recipient.address} />
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
